/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, FileText, Save, AlertTriangle } from 'lucide-react';
import {
  knowledgeBaseService,
  AI_AGENT_ERROR_CODES,
  getApiErrorMessage,
  KB_DOCUMENT_SYSTEM_METADATA_KEYS,
  KB_DOCUMENT_FINAL_STATUSES,
} from '../services/knowledgeBaseService';
import useDocumentMetadataForm from '../hooks/useDocumentMetadataForm';
import DocumentMetadataFields, { MetadataSectionHeader } from '../components/knowledgeBases/DocumentMetadataFields';
import AgenticAiUnavailable from '../components/aiAgents/AgenticAiUnavailable';
import StatusBadge from '../components/StatusBadge';
import { getAgenticAiServiceError } from '../utils/agenticAiErrors';
import toastService from '../utils/toastService';
import { useAuth } from '../context/AuthContext';
import { ACCOUNT_LABEL } from '../constants/entityLabels';

const LIST_ROUTE = '/knowledge-bases';

/**
 * Edits the metadata of one knowledge base document: the service request type
 * hierarchy (pre-selected when the server matched one) and the custom pairs.
 * System keys (file name, page count, public link...) are not shown; the
 * server keeps them.
 */
const EditKnowledgeBaseDocument = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: knowledgeBaseId, documentId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  // Platform-wide view carries the account in the URL; account / sub-account users use their own.
  const showAccountField = !user?.accountUID;
  const accountId = showAccountField ? (searchParams.get('accountId') || '') : user?.accountUID;

  const [knowledgeBaseName, setKnowledgeBaseName] = useState(location.state?.knowledgeBase?.name || '');
  const [kbDocument, setKbDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serviceError, setServiceError] = useState(null);
  const [integrationError, setIntegrationError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const form = useDocumentMetadataForm(accountId, { reservedKeys: KB_DOCUMENT_SYSTEM_METADATA_KEYS });
  const { prefill } = form;

  // Back / cancel / success all return to this knowledge base's documents list.
  const goToDocuments = () => {
    const query = showAccountField && accountId ? `?accountId=${encodeURIComponent(accountId)}` : '';
    navigate(`${LIST_ROUTE}/${encodeURIComponent(knowledgeBaseId)}/documents${query}`, {
      state: { knowledgeBase: { id: knowledgeBaseId, name: knowledgeBaseName } },
    });
  };

  useEffect(() => {
    if (user && showAccountField && !accountId) {
      toastService.error(`Select ${ACCOUNT_LABEL.withArticle} first`);
      navigate(LIST_ROUTE, { replace: true });
    }
  }, [user, showAccountField, accountId, navigate]);

  // Only the latest load may prefill the form (StrictMode / param changes start overlapping loads).
  const requestIdRef = useRef(0);
  const fetchDocument = async () => {
    if (!accountId) { return; }
    const currentRequestId = ++requestIdRef.current;
    try {
      setLoading(true);
      setServiceError(null);
      setIntegrationError(null);
      const { data } = await knowledgeBaseService.getDocument(
        knowledgeBaseId,
        documentId,
        showAccountField ? { accountId } : {}
      );
      if (currentRequestId !== requestIdRef.current) { return; }
      const doc = data?.data;
      setKbDocument(doc || null);
      if (data?.knowledgeBase?.name) { setKnowledgeBaseName(data.knowledgeBase.name); }
      prefill({
        metadata: doc?.metadata || {},
        serviceRequestTypeUid: doc?.hierarchy?.serviceRequestTypeUid,
        hierarchyNodeUid: doc?.hierarchy?.hierarchyNodeUid,
      });
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) { return; }
      const message = getApiErrorMessage(err, 'Failed to load document');
      if (getAgenticAiServiceError(err)) {
        setServiceError(getAgenticAiServiceError(err));
      } else if (AI_AGENT_ERROR_CODES.includes(err.response?.data?.code)) {
        setIntegrationError(message);
      } else {
        toastService.error(message);
      }
      setKbDocument(null);
    } finally {
      if (currentRequestId === requestIdRef.current) { setLoading(false); }
    }
  };

  useEffect(() => {
    fetchDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knowledgeBaseId, documentId, accountId]);

  const isProcessing = Boolean(kbDocument) && !KB_DOCUMENT_FINAL_STATUSES.includes(kbDocument.status);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.validate()) { return; }

    const payload = { metadata: form.customMetadata };
    if (showAccountField) { payload.accountId = accountId; }
    if (form.typeUid) { payload.serviceRequestTypeUid = form.typeUid; }
    if (form.leafUid) { payload.hierarchyNodeUid = form.leafUid; }

    try {
      setSubmitting(true);
      await knowledgeBaseService.updateDocumentMetadata(knowledgeBaseId, documentId, payload);
      toastService.success('Document metadata updated successfully');
      goToDocuments();
    } catch (err) {
      toastService.error(getApiErrorMessage(err, 'Failed to update document'));
    } finally {
      setSubmitting(false);
    }
  };

  const details = kbDocument
    ? [kbDocument.fileType?.toUpperCase(), kbDocument.pageCount ? `${kbDocument.pageCount} page${kbDocument.pageCount === 1 ? '' : 's'}` : null]
      .filter(Boolean)
      .join(' · ')
    : '';

  const renderBody = () => {
    if (loading) {
      return (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-base-200/50 rounded-2xl animate-pulse"></div>
          ))}
        </div>
      );
    }
    if (serviceError) {
      return <AgenticAiUnavailable error={serviceError} onRetry={fetchDocument} />;
    }
    if (integrationError) {
      return (
        <div role="alert" className="alert alert-warning rounded-xl text-sm font-semibold">
          <AlertTriangle size={18} className="shrink-0" />
          <span>{integrationError}</span>
        </div>
      );
    }
    if (!kbDocument) {
      return (
        <div role="alert" className="alert alert-warning rounded-xl text-sm font-semibold">
          <AlertTriangle size={18} className="shrink-0" />
          <span>The document could not be loaded.</span>
        </div>
      );
    }
    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Document (read-only) */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <MetadataSectionHeader icon={FileText} title="Document" badge={null} />
          <div className="flex items-center justify-between gap-3 p-4 rounded-xl bg-base-200/50 border border-base-content/10">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-error/10 text-error flex items-center justify-center shrink-0">
                <FileText size={20} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-base-content break-words">{kbDocument.name}</p>
                {details && <p className="text-xs text-base-content/50">{details}</p>}
              </div>
            </div>
            <StatusBadge status={kbDocument.status || 'unknown'} />
          </div>
          {isProcessing && (
            <div role="alert" className="alert alert-warning rounded-xl text-xs font-semibold">
              <AlertTriangle size={16} className="shrink-0" />
              <span>This document is still being processed. Metadata can be edited once processing finishes.</span>
            </div>
          )}
        </div>

        <DocumentMetadataFields form={form} disabled={submitting || isProcessing} />

        {/* Form Actions */}
        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-2">
          <button
            type="button"
            onClick={goToDocuments}
            className="w-full sm:w-auto px-6 py-3 border border-base-content/20 text-base-content/70 hover:bg-base-200 rounded-xl font-bold text-sm transition-all"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || isProcessing}
            className="w-full sm:flex-1 btn-premium justify-center"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-primary-content/30 border-t-primary-content rounded-full animate-spin"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save size={16} />
                <span>Save Metadata</span>
              </>
            )}
          </button>
        </div>
      </form>
    );
  };

  return (
    <div className="space-y-6 p-1 sm:p-2 w-full pb-12">
      {/* Top Breadcrumb */}
      <div>
        <button
          type="button"
          onClick={goToDocuments}
          className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-base-content/50 hover:text-primary transition-colors group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to Documents
        </button>
      </div>

      {/* Hero Header Banner */}
      <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10">
        <div className="space-y-1">
          <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40">
            Edit Document Metadata
          </span>
          <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight break-words">
            {knowledgeBaseName || 'Knowledge Base'}
          </h1>
          <p className="text-xs text-base-content/50">
            Update the hierarchy and key / value metadata the AI Agent uses for this document.
          </p>
        </div>
      </div>

      {renderBody()}
    </div>
  );
};

export default EditKnowledgeBaseDocument;
